import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, ChevronLeft, ChevronRight, Users } from "lucide-react";
import toast from "react-hot-toast";
import tokenService from "../../../services/token-service";
import { ServiceCategoryModal, HandymanProfileModal } from "../modals/index";
import MakePaymentDialog from "../modals/make-payment-dialog";
import RatingDialog from "../modals/rating-dialog";
import Chat from "./chat";
import { approveWorkCompletion, confirmJobCompletion } from "../../../services/scheduled-tasks-api";
import ScheduledTaskCard from "./scheduled-task-card";
import HandymanRequestCard from "./handyman-request-card";
import StatusConnector from "./status-connector";

/**
 * Schedule Component
 * 
 * Main component for displaying customer's scheduled tasks and handyman requests.
 * 
 * Layout Structure:
 * - Left side (30-40%): Scheduled task card with task details
 * - Middle: Status connector line showing task status
 * - Right side (60-70%): Container with handyman requests (paginated, 5 per page)
 * 
 * Features:
 * - Displays scheduled tasks with pagination
 * - Shows handyman requests for each task
 * - Visual status indicator connecting task to requests
 * - Accept/reject handyman offers
 * - Navigate to task details
 * - Chat with handymen
 * 
 * @returns {JSX.Element} Schedule screen component
 */
const Schedule = () => {
  const navigate = useNavigate();

  // State management
  const [scheduledTasks, setScheduledTasks] = useState([]); // All scheduled tasks
  const [loading, setLoading] = useState(true); // Loading state for data fetch
  const [isModalOpen, setIsModalOpen] = useState(false); // Service category modal state
  const [activeChat, setActiveChat] = useState(null); // Active chat session
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isRatingModalOpen, setIsRatingModalOpen] = useState(false);
  const [activeTaskForAction, setActiveTaskForAction] = useState(null);

  // Pagination state for scheduled tasks list
  const [currentTaskPage, setCurrentTaskPage] = useState(1);
  const tasksPerPage = 5; // Show 5 tasks per page

  // Pagination state for handyman requests (per task)
  const [requestPages, setRequestPages] = useState({}); // { taskId: currentPage }
  const requestsPerPage = 5; // Show 5 requests per page

  // Get authentication token and user info
  // eslint-disable-next-line no-unused-vars
  const token = tokenService.getToken();
  const authUser = localStorage.getItem("authUser")
    ? JSON.parse(localStorage.getItem("authUser"))
    : null;
  // eslint-disable-next-line no-unused-vars
  const userId = authUser?._id;

  // Fetch scheduled tasks on component mount
  useEffect(() => {
    fetchScheduledTasks();
  }, []);

  /**
   * Fetch scheduled tasks from API
   * Fetches all scheduled tasks for the logged-in customer with handyman offers
   */
  const fetchScheduledTasks = async () => {
    setLoading(true);
    try {
      // API call to fetch scheduled tasks
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/services/customer/scheduled-tasks`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      // Check if response is ok
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Check if API call was successful
      if (data.success) {
        // Set scheduled tasks from API response
        setScheduledTasks(data.tasks || []);

        // Initialize request pages for each task (default to page 1)
        const initialPages = {};
        (data.tasks || []).forEach((task) => {
          initialPages[task.id] = 1;
        });
        setRequestPages(initialPages);
      } else {
        throw new Error(data.message || "Failed to fetch scheduled tasks");
      }
    } catch (error) {
      console.error("Error fetching scheduled tasks:", error);
      toast.error(error.message || "Failed to load scheduled tasks. Please try again.");
      // Set empty array on error
      setScheduledTasks([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle task card click - navigate to detail page
   * @param {string} taskId - ID of the task to view
   */
  const handleTaskClick = (taskId) => {
    navigate(`/customer/schedule/${taskId}`);
  };

  // Modal state
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  /**
   * Handle accept handyman request
   * @param {Object} task - The task object
   * @param {Object} request - The handyman request to accept
   */
  const handleAcceptHandyman = async (task, request) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/services/customer/offers/accept`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          taskId: task.id || task._id,
          offerId: request.id || request._id || request.offerId
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success("Handyman accepted successfully!");
        // Update local state to reflect acceptance
        setScheduledTasks((prevTasks) =>
          prevTasks.map((t) =>
            t.id === task.id
              ? {
                ...t,
                status: "handyman_selected",
                handymanRequests: t.handymanRequests.map((r) =>
                  r.id === request.id
                    ? { ...r, requestStatus: "accepted", isSelected: true }
                    : { ...r, requestStatus: "rejected", isSelected: false }
                ),
              }
              : t
          )
        );
      } else {
        throw new Error(data.message || "Failed to accept offer");
      }
    } catch (error) {
      console.error("Error accepting handyman:", error);
      toast.error(error.message || "Failed to accept offer");
    }
  };

  /**
   * Handle reject handyman request
   * @param {Object} task - The task object
   * @param {Object} request - The handyman request to reject
   */
  const handleRejectHandyman = (task, request) => {
    console.log("Rejecting handyman:", request.handyman.name, "for task:", task.id);
    // TODO: Implement API call to reject handyman if needed
    toast.success("Offer rejected");

    // Update local state
    setScheduledTasks((prevTasks) =>
      prevTasks.map((t) =>
        t.id === task.id
          ? {
            ...t,
            handymanRequests: t.handymanRequests.map((r) =>
              r.id === request.id ? { ...r, requestStatus: "rejected" } : r
            ),
          }
          : t
      )
    );
  };

  /**
   * Handle view handyman profile
   * @param {Object} request - The handyman request
   */
  const handleViewProfile = (request) => {
    setSelectedProfile(request.handyman);
    setIsProfileModalOpen(true);
  };

  /**
   * Handle work approval
   */
  const handleApproveWork = async (task) => {
    try {
      const response = await approveWorkCompletion(task.id || task._id);
      if (response.success) {
        toast.success("Work approved! Please proceed to payment.");
        // Refresh tasks
        fetchScheduledTasks();
      } else {
        throw new Error(response.error || "Failed to approve work");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Handle payment submission
   */
  const handleMakePayment = (task) => {
    setActiveTaskForAction(task);
    setIsPaymentModalOpen(true);
  };

  const onConfirmPayment = async (paymentData) => {
    try {
      // For now, we'll just simulate the payment call or use a placeholder
      // The user wants to "submit" payment details
      console.log("Submitting payment status for task:", activeTaskForAction.id, paymentData);

      // In a real scenario, we'd call a payment API here. 
      // For now, we notify the user and wait for handyman confirmation.
      toast.success("Payment details submitted! Waiting for handyman to confirm receipt.");

      // We might want to update local status to something like 'payment_transferred' 
      // but based on handyman controller, they check status 'approved' etc.
      // fetchScheduledTasks();
    } catch (error) {
      toast.error("Failed to submit payment details");
    }
  };

  /**
   * Handle final completion and rating
   */
  const handleFinalComplete = (task) => {
    setActiveTaskForAction(task);
    setIsRatingModalOpen(true);
  };

  const onConfirmRating = async ({ rating, review }) => {
    try {
      const response = await confirmJobCompletion(activeTaskForAction.id, rating, review);
      if (response.success) {
        toast.success("Task completed successfully! Thank you for your feedback.");
        fetchScheduledTasks();
      } else {
        throw new Error(response.error || "Failed to complete task");
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  /**
   * Change page for handyman requests of a specific task
   * @param {string} taskId - ID of the task
   * @param {number} newPage - New page number
   */
  const changeRequestPage = (taskId, newPage) => {
    setRequestPages((prev) => ({
      ...prev,
      [taskId]: newPage,
    }));
  };

  /**
   * Get paginated handyman requests for a task
   * @param {Object} task - The task object
   * @returns {Array} Paginated requests
   */
  const getPaginatedRequests = (task) => {
    const currentPage = requestPages[task.id] || 1;
    const startIndex = (currentPage - 1) * requestsPerPage;
    const endIndex = startIndex + requestsPerPage;
    return task.handymanRequests.slice(startIndex, endIndex);
  };

  /**
   * Get total pages for handyman requests of a task
   * @param {Object} task - The task object
   * @returns {number} Total pages
   */
  const getTotalRequestPages = (task) => {
    return Math.ceil(task.handymanRequests.length / requestsPerPage);
  };

  // Calculate pagination for scheduled tasks
  const totalTaskPages = Math.ceil(scheduledTasks.length / tasksPerPage);
  const startTaskIndex = (currentTaskPage - 1) * tasksPerPage;
  const endTaskIndex = startTaskIndex + tasksPerPage;
  const currentTasks = scheduledTasks.slice(startTaskIndex, endTaskIndex);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-[#EFA938] border-t-transparent"></div>
          <p className="text-gray-600 dark:text-gray-400 text-sm font-medium">
            Loading schedule...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 px-6 py-4 shadow-sm rounded-lg">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Scheduled Services
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1">
              View and manage your scheduled service appointments
            </p>
            {scheduledTasks.length > 0 && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {scheduledTasks.length} appointment{scheduledTasks.length !== 1 ? "s" : ""}{" "}
                scheduled
              </p>
            )}
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-6 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors shadow-md hover:shadow-lg"
          >
            New Request
          </button>
        </div>
      </div>

      {/* Main Content - Scheduled Tasks */}
      <div className="rounded-2xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
        {/* Section Header */}
        <header className="px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 flex items-center gap-3">
          <Calendar className="h-5 w-5 text-[#EFA938]" />
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Scheduled Tasks & Handyman Requests
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Review tasks and manage handyman offers
            </p>
          </div>
        </header>

        {/* Tasks List */}
        <div className="divide-y divide-gray-100 dark:divide-slate-700">
          {currentTasks.length > 0 ? (
            currentTasks.map((task) => (
              <div key={task.id} className="p-6">
                {/* Task Row: Left (Task Card) + Middle (Connector) + Right (Requests) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                  {/* Left Side - Scheduled Task Card (30-40% width on large screens) */}
                  <div className="lg:col-span-4">
                    <ScheduledTaskCard
                      task={task}
                      onClick={() => handleTaskClick(task.id)}
                    />
                  </div>

                  {/* Middle - Status Connector (hidden on mobile) */}
                  <div className="hidden lg:flex lg:col-span-1 items-center justify-center">
                    <StatusConnector
                      status={task.status}
                      animated={
                        task.status === "pending_offers" ||
                        task.status === "searching" ||
                        task.status === "in_progress"
                      }
                    />
                  </div>

                  {/* Right Side - Handyman Requests Container (60-70% width on large screens) */}
                  <div className="lg:col-span-7">
                    <div className="rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 overflow-hidden">
                      {/* Requests Header */}
                      <div className="px-5 py-3 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-b border-gray-200 dark:border-slate-700">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Users className="h-5 w-5 text-[#EFA938]" />
                            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                              Handyman Requests
                            </h3>
                          </div>
                          <span className="px-3 py-1 rounded-full bg-[#EFA938] text-white text-xs font-bold">
                            {task.handymanRequests.length}
                          </span>
                        </div>
                      </div>

                      {/* Requests List */}
                      <div className="p-4">
                        {task.handymanRequests.length > 0 ? (
                          <>
                            {/* Request Cards */}
                            <div className="space-y-3">
                              {getPaginatedRequests(task).map((request) => (
                                <HandymanRequestCard
                                  key={request.id}
                                  request={request}
                                  isSelected={request.isSelected}
                                  onAccept={() => handleAcceptHandyman(task, request)}
                                  onReject={() => handleRejectHandyman(task, request)}
                                  onViewProfile={() => handleViewProfile(request)}
                                  taskStatus={task.status}
                                  onApproveWork={() => handleApproveWork(task)}
                                  onMakePayment={() => handleMakePayment(task)}
                                  onFinalComplete={() => handleFinalComplete(task)}
                                />
                              ))}
                            </div>

                            {/* Pagination Controls for Requests */}
                            {getTotalRequestPages(task) > 1 && (
                              <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-slate-700">
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                  Page {requestPages[task.id] || 1} of{" "}
                                  {getTotalRequestPages(task)}
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() =>
                                      changeRequestPage(
                                        task.id,
                                        (requestPages[task.id] || 1) - 1
                                      )
                                    }
                                    disabled={(requestPages[task.id] || 1) === 1}
                                    className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <ChevronLeft className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      changeRequestPage(
                                        task.id,
                                        (requestPages[task.id] || 1) + 1
                                      )
                                    }
                                    disabled={
                                      (requestPages[task.id] || 1) ===
                                      getTotalRequestPages(task)
                                    }
                                    className="p-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                  >
                                    <ChevronRight className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          // No requests yet
                          <div className="text-center py-8">
                            <Users className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                            <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                              No handyman requests yet
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                              Waiting for handymen to respond to your task
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            // No scheduled tasks
            <div className="px-6 py-12 text-center">
              <Calendar className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                No scheduled services
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Schedule a service to see your appointments here
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-6 py-2 rounded-lg bg-[#EFA938] text-white font-semibold hover:bg-[#d78c1f] transition-colors"
              >
                Schedule Service
              </button>
            </div>
          )}
        </div>

        {/* Pagination Controls for Tasks */}
        {totalTaskPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentTaskPage} of {totalTaskPages} ({scheduledTasks.length} total
                tasks)
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentTaskPage((prev) => prev - 1)}
                  disabled={currentTaskPage === 1}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </button>
                <button
                  onClick={() => setCurrentTaskPage((prev) => prev + 1)}
                  disabled={currentTaskPage === totalTaskPages}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Service Category Modal */}
      <ServiceCategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />

      <HandymanProfileModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        handyman={selectedProfile}
      />

      <MakePaymentDialog
        isOpen={isPaymentModalOpen}
        onClose={() => setIsPaymentModalOpen(false)}
        totalPrice={activeTaskForAction?.price}
        onConfirm={onConfirmPayment}
      />

      <RatingDialog
        isOpen={isRatingModalOpen}
        onClose={() => setIsRatingModalOpen(false)}
        handymanName={activeTaskForAction?.handyman?.name || "Handyman"}
        onConfirm={onConfirmRating}
      />

      {/* Chat Component for Scheduled Jobs */}
      {activeChat && (
        <Chat
          handymanName={activeChat.handymanName}
          handymanAvatar={activeChat.handymanAvatar}
          isLive={true}
          onEndChat={() => {
            setActiveChat(null);
          }}
        />
      )}
    </div>
  );
};

export default Schedule;
